import React, { useState, useEffect, useRef } from 'react';
import { getRequest } from './services/apiService';
import { ChevronDown, X, Eye, FileText, Image, Settings } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Paginator } from 'primereact/paginator';
import * as saveSvgAsPng from 'save-svg-as-png';
import ReusableForm from './common/ReusableForm';

function HullEquipmentStatusMatrix({ data, projectId }) {
  const [equipmentList, setEquipmentList] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [matrixData, setMatrixData] = useState([]);
  const [statusHeaders, setStatusHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [equipmentModalLoading, setEquipmentModalLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);  
  // Pagination state for equipment modal
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [tempSelectedEquipment, setTempSelectedEquipment] = useState([]);
  
  const tableRef = useRef(null);

  // Fetch equipment list for modal with search
useEffect(() => {
  const fetchEquipment = async () => {
    setLoading(true);
    try {
      const url = searchQuery 
        ? `/master/equipment/?search=${encodeURIComponent(searchQuery)}`
        : '/master/equipment/';
      
      const response = await getRequest(url);
      
      if (response?.data) {
        setEquipmentList(response.data);
      } else if (Array.isArray(response)) {
        setEquipmentList(response);
      } else {
        setEquipmentList([]);
      }
    } catch (error) {
      console.error('Error fetching equipment list:', error);
      setEquipmentList([]);
    } finally {
      setLoading(false);
    }
  };

  const debounceTimer = setTimeout(() => {
    fetchEquipment();
  }, 500); // 500ms debounce

  return () => clearTimeout(debounceTimer);
}, [searchQuery]);

  // Extract dynamic headers from API response
  useEffect(() => {
    if (matrixData.length > 0) {
      const firstItem = matrixData[0];
      const excludedKeys = new Set(['ser', 'eqpt', 'remarks', 'equipment_name']);
      const headers = Object.keys(firstItem).filter(
        key => !excludedKeys.has(key.toLowerCase())
      );
      setStatusHeaders(headers);
    } else {
      setStatusHeaders([]);
    }
  }, [matrixData]);

  // Fetch matrix data when equipment selection changes
  // Fetch matrix data when equipment selection changes
useEffect(() => {
  if (!projectId) {
    setMatrixData([]);
    return;
  }

  setLoading(true);
  // MODIFICATION START
  const equipmentIds = selectedEquipment.length > 0 ? selectedEquipment.join(',') : '1'; 
  // MODIFICATION END
  getRequest(`/transaction/multiple-equipment-data-chart/?project_id=${projectId}&equipment_id=${equipmentIds}`)
    .then(response => {
      const data = response?.data || response || [];
      setMatrixData(data);
    })
    .catch(error => {
      console.error('Error fetching matrix data:', error);
      setMatrixData([]);
    })
    .finally(() => setLoading(false));
}, [projectId, selectedEquipment]);

  const getStatusColor = (value) => {
    const num = parseInt(value, 10);
    switch (num) {
      case 3: return '#ef4444'; // red
      case 2: return '#eab308'; // yellow
      case 1: return '#22c55e'; // green
      default: return '#6b7280'; // gray
    }
  };

  const getStatusText = (value) => {
    const num = parseInt(value, 10);
    switch (num) {
      case 3: return 'Critical';
      case 2: return 'Warning';
      case 1: return 'Good';
      default: return 'Unknown';
    }
  };

  const formatHeader = (header) => {
    return header
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleOpenEquipmentModal = () => {
    setTempSelectedEquipment([...selectedEquipment]);
    setCurrentPage(0);
    setShowEquipmentModal(true);
  };

  const handleEquipmentToggle = (equipmentId) => {
    setTempSelectedEquipment(prev => 
      prev.includes(equipmentId) 
        ? prev.filter(id => id !== equipmentId)
        : [...prev, equipmentId]
    );
  };

  const handleSelectAllPage = (pageEquipment, isChecked) => {
    if (isChecked) {
      const pageIds = pageEquipment.map(eq => eq.id);
      setTempSelectedEquipment(prev => [...new Set([...prev, ...pageIds])]);
    } else {
      const pageIds = pageEquipment.map(eq => eq.id);
      setTempSelectedEquipment(prev => prev.filter(id => !pageIds.includes(id)));
    }
  };

  const handleSaveEquipmentSelection = () => {
    setEquipmentModalLoading(true);
    
    // Update selected equipment
    setSelectedEquipment([...tempSelectedEquipment]);
    
    // Close modal
    setTimeout(() => {
      setEquipmentModalLoading(false);
      setShowEquipmentModal(false);
    }, 500);
  };

  const handleCancelEquipmentSelection = () => {
    setTempSelectedEquipment([...selectedEquipment]);
    setShowEquipmentModal(false);
  };

  const removeEquipment = (id) => {
    setSelectedEquipment(prev => prev.filter(item => item !== id));
  };

  const clearAll = () => {
    setSelectedEquipment([]);
  };

  // Get current page equipment for pagination
  const getCurrentPageEquipment = () => {
    const start = currentPage * rowsPerPage;
    const end = start + rowsPerPage;
    return equipmentList.slice(start, end);
  };

  const onPageChange = (event) => {
    setCurrentPage(event.page);
  };

  const renderEquipmentModal = () => {
    const currentPageEquipment = getCurrentPageEquipment();
  const allPageSelected = currentPageEquipment.length > 0 && 
    currentPageEquipment.every(eq => tempSelectedEquipment.includes(eq.id));
  const somePageSelected = currentPageEquipment.some(eq => tempSelectedEquipment.includes(eq.id));
    return (
   <Dialog
  visible={showEquipmentModal}
  onHide={handleCancelEquipmentSelection}
  header="Select Equipment"
  style={{ width: '60vw' }}
  draggable={false}
  modal
  className="p-fluid modern-equipment-dialog"
>
  <div className="flex flex-col h-full space-y-6">
     <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <i className="pi pi-search text-gray-400"></i>
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all"
            placeholder="Search equipment..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <i className="pi pi-times text-gray-400 hover:text-gray-600 cursor-pointer"></i>
            </button>
          )}
        </div>

    {/* Combined Section - 50/50 Layout */}
    <div className="flex gap-4">
      {/* Left: Selection Summary - 50% */}
      <div className="flex-1 relative overflow-hidden bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50"></div>
        <div className="relative p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center justify-center w-12 h-12 bg-emerald-500 rounded-xl shadow-lg">
                <i className="pi pi-shopping-cart text-white text-lg"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {tempSelectedEquipment.length} Selected
                </h3>
                <p className="text-sm text-gray-600">Equipment ready for assignment</p>
              </div>
            </div>
            {tempSelectedEquipment.length > 0 && (
              <button
                onClick={() => setTempSelectedEquipment([])}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 hover:border-red-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                <i className="pi pi-times mr-2"></i>
                Clear All
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Right: Select All Section - 50% */}
      <div className="flex-1 flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-xl">
        <div className="flex items-center space-x-3">
          <div className="p-1 border-2 border-gray-300 rounded-lg bg-white">
            <Checkbox
              inputId="selectAllPage"
              checked={allPageSelected}
              indeterminate={!allPageSelected && somePageSelected}
              onChange={(e) => handleSelectAllPage(currentPageEquipment, e.checked)}
              className="border-gray-400"
            />
          </div>
          <label htmlFor="selectAllPage" className="text-sm font-semibold text-gray-700 cursor-pointer">
            Select All on This Page
          </label>
        </div>
        <span className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-600 bg-gray-200 rounded-full">
          {currentPageEquipment.length} items
        </span>
      </div>
    </div>

    {/* Equipment List - 2 Column Grid Design */}
    <div className="flex-1 max-h-fulloverflow-hidden">
      <div className="grid grid-cols-2 gap-4 h-full">
        {currentPageEquipment.map((equipment) => (
          <div 
            key={equipment.id} 
            className={`
              group relative p-4 bg-white border rounded-xl transition-all duration-200 cursor-pointer
              hover:border-blue-300 hover:shadow-md hover:-translate-y-0.5
              ${tempSelectedEquipment.includes(equipment.id) 
                ? 'border-emerald-400 bg-emerald-50 shadow-md ring-1 ring-emerald-400' 
                : 'border-gray-200'
              }
            `}
            onClick={() => handleEquipmentToggle(equipment.id)}
          >
            <div className="flex items-center space-x-3">
              <div className={`
                p-1 border-2 rounded-lg transition-all duration-200 flex-shrink-0
                ${tempSelectedEquipment.includes(equipment.id) 
                  ? 'border-emerald-400 bg-emerald-50' 
                  : 'border-gray-300 bg-white'
                }
              `}>
                <Checkbox
                  inputId={`equipment-${equipment.id}`}
                  checked={tempSelectedEquipment.includes(equipment.id)}
                  onChange={() => handleEquipmentToggle(equipment.id)}
                  className={`
                    ${tempSelectedEquipment.includes(equipment.id) 
                      ? 'border-emerald-500' 
                      : 'border-gray-400'
                    }
                  `}
                />
              </div>
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <div className={`
                  flex items-center justify-center w-8 h-8 rounded-lg transition-colors flex-shrink-0
                  ${tempSelectedEquipment.includes(equipment.id) 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-gray-100 text-gray-500 group-hover:bg-blue-100 group-hover:text-blue-500'
                  }
                `}>
                  <i className="pi pi-wrench text-xs"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <label 
                    htmlFor={`equipment-${equipment.id}`} 
                    className="block text-sm font-medium text-gray-900 cursor-pointer truncate"
                    title={equipment.name}
                  >
                    {equipment.name}
                  </label>
                </div>
              </div>
              {tempSelectedEquipment.includes(equipment.id) && (
                <div className="flex items-center justify-center w-5 h-5 bg-emerald-500 rounded-full flex-shrink-0">
                  <i className="pi pi-check text-white text-xs"></i>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {currentPageEquipment.length === 0 && (
        <div className="col-span-2 flex flex-col items-center justify-center py-16 text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <i className="pi pi-search text-2xl text-gray-400"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No equipment found</h3>
          <p className="text-sm text-gray-500">Try adjusting your search or filter criteria</p>
        </div>
      )}
    </div>

    {/* Pagination - Modern Style */}
    {equipmentList.length > rowsPerPage && (
      <div className="flex justify-center p-0 bg-gray-50 border border-gray-200 rounded-xl">
        <Paginator
          first={currentPage * rowsPerPage}
          rows={rowsPerPage}
          totalRecords={equipmentList.length}
          onPageChange={onPageChange}
          template="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
        />
      </div>
    )}

    {/* Action Buttons - Beautiful Modern Design */}
    <div className="flex items-center justify-end space-x-4 pt-2 border-t border-gray-200">
      <button
        onClick={handleCancelEquipmentSelection}
        disabled={equipmentModalLoading}
        className="inline-flex items-center px-6 py-3 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
      >
        <i className="pi pi-times mr-2"></i>
        Cancel
      </button>
      
      <button
        onClick={handleSaveEquipmentSelection}
        disabled={equipmentModalLoading}
        className="inline-flex items-center px-8 py-3 text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-600 border border-transparent rounded-xl hover:from-emerald-600 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
      >
        {equipmentModalLoading ? (
          <>
            <i className="pi pi-spinner pi-spin mr-2"></i>
            Saving...
          </>
        ) : (
          <>
            <i className="pi pi-check mr-2"></i>
            Save Selection
          </>
        )}
      </button>
    </div>
  </div>
</Dialog>
    );
  };

  const handleDownloadCSV = () => {
    if (matrixData.length === 0) {
      alert('No data available to download');
      return;
    }

    const csvContent = [
      ['Equipment', ...statusHeaders.map(formatHeader), 'Remarks'].join(','),
      ...matrixData.map(item => [
        `"${item.eqpt || item.equipment_name || ''}"`,
        ...statusHeaders.map(h => getStatusText(item[h])),
        `"${item.remarks || '-'}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'hull-equipment-status-matrix.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPNG = () => {
    if (matrixData.length === 0) {
      alert('No data available to download');
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Calculate dimensions
    const equipmentColWidth = 250;
    const statusColWidth = 140;
    const headerHeight = 60;
    const rowHeight = 50;
    const padding = 10;
    
    const totalWidth = equipmentColWidth + (statusHeaders.length * statusColWidth);
    const totalHeight = headerHeight + (matrixData.length * rowHeight);
    
    // Set canvas size with high DPI for better quality
    const dpiScale = 2;
    canvas.width = totalWidth * dpiScale;
    canvas.height = (totalHeight + 80) * dpiScale;
    ctx.scale(dpiScale, dpiScale);
    
    // Fill entire canvas with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, totalWidth, totalHeight + 80);
    
    // Set default text properties
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Draw header background (dark gray-800)
    ctx.fillStyle = '#1f2937';
    ctx.fillRect(0, 0, totalWidth, headerHeight);
    
    // Draw header text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 14px Arial, sans-serif';
    
    // Equipment header
    ctx.textAlign = 'center';
    ctx.fillText('Major Eqpt', equipmentColWidth / 2, headerHeight / 2);
    
    // Status headers
    statusHeaders.forEach((header, i) => {
      const x = equipmentColWidth + (i * statusColWidth) + (statusColWidth / 2);
      const formattedHeader = formatHeader(header);
      
      const maxCharsPerLine = 12;
      if (formattedHeader.length > maxCharsPerLine) {
        const words = formattedHeader.split(' ');
        let lines = [];
        let currentLine = '';
        
        words.forEach(word => {
          if ((currentLine + word).length <= maxCharsPerLine) {
            currentLine += (currentLine ? ' ' : '') + word;
          } else {
            if (currentLine) lines.push(currentLine);
            currentLine = word;
          }
        });
        if (currentLine) lines.push(currentLine);
        
        lines.forEach((line, lineIndex) => {
          const lineY = headerHeight / 2 + (lineIndex - (lines.length - 1) / 2) * 16;
          ctx.fillText(line, x, lineY);
        });
      } else {
        ctx.fillText(formattedHeader, x, headerHeight / 2);
      }
    });
    
    // Draw header borders
    ctx.strokeStyle = '#4b5563';
    ctx.lineWidth = 1;
    ctx.beginPath();
    
    ctx.moveTo(equipmentColWidth, 0);
    ctx.lineTo(equipmentColWidth, headerHeight);
    statusHeaders.forEach((_, i) => {
      const x = equipmentColWidth + ((i + 1) * statusColWidth);
      ctx.moveTo(x, 0);
      ctx.lineTo(x, headerHeight);
    });
    
    ctx.moveTo(0, headerHeight);
    ctx.lineTo(totalWidth, headerHeight);
    ctx.stroke();
    
    // Draw data rows
    matrixData.forEach((item, rowIndex) => {
      const y = headerHeight + (rowIndex * rowHeight);
      
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, y, equipmentColWidth, rowHeight);
      
      ctx.fillStyle = '#111827';
      ctx.font = '13px Arial, sans-serif';
      ctx.textAlign = 'left';
      
      const equipmentName = item.eqpt || item.equipment_name || '';
      const maxWidth = equipmentColWidth - 20;
      let displayName = equipmentName;
      
      ctx.font = '13px Arial, sans-serif';
      if (ctx.measureText(displayName).width > maxWidth) {
        while (ctx.measureText(displayName + '...').width > maxWidth && displayName.length > 0) {
          displayName = displayName.slice(0, -1);
        }
        displayName += '...';
      }
      
      ctx.fillText(displayName, 10, y + (rowHeight / 2));
      
      statusHeaders.forEach((header, i) => {
        const x = equipmentColWidth + (i * statusColWidth);
        const statusColor = getStatusColor(item[header]);
        
        ctx.fillStyle = statusColor;
        ctx.fillRect(x, y, statusColWidth, rowHeight);
      });
      
      ctx.strokeStyle = '#d1d5db';
      ctx.lineWidth = 1;
      ctx.beginPath();
      
      ctx.moveTo(0, y + rowHeight);
      ctx.lineTo(totalWidth, y + rowHeight);
      
      ctx.moveTo(equipmentColWidth, y);
      ctx.lineTo(equipmentColWidth, y + rowHeight);
      statusHeaders.forEach((_, i) => {
        const lineX = equipmentColWidth + ((i + 1) * statusColWidth);
        ctx.moveTo(lineX, y);
        ctx.lineTo(lineX, y + rowHeight);
      });
      ctx.stroke();
    });
    
    // Draw outer border
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, totalWidth, totalHeight);
    
    // Add legend
    const legendY = totalHeight + 30;
    const legendItemWidth = 100;
    
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, totalHeight, totalWidth, 80);
    
    ctx.fillStyle = '#374151';
    ctx.font = 'bold 14px Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Status Legend:', 20, legendY);
    
    const legendItems = [
      ['#22c55e', 'Good'],
      ['#eab308', 'Warning'], 
      ['#ef4444', 'Critical'],
      ['#6b7280', 'Unknown']
    ];
    
    ctx.font = '12px Arial, sans-serif';
    legendItems.forEach(([color, label], i) => {
      const x = 150 + (i * legendItemWidth);
      
      ctx.fillStyle = color;
      ctx.fillRect(x, legendY - 8, 16, 16);
      
      ctx.strokeStyle = '#374151';
      ctx.lineWidth = 1;
      ctx.strokeRect(x, legendY - 8, 16, 16);
      
      ctx.fillStyle = '#374151';
      ctx.textAlign = 'left';
      ctx.fillText(label, x + 22, legendY);
    });
    
    canvas.toBlob((blob) => {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'hull-equipment-status-matrix.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }, 'image/png');
  };

  const renderDetailsTable = () => {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Equipment</th>
              {statusHeaders.map(header => (
                <th key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {formatHeader(header)}
                </th>
              ))}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Overall Status</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {matrixData.map((item, idx) => {
              const statusValues = statusHeaders.map(h => parseInt(item[h], 10));
              const criticalCount = statusValues.filter(s => s === 3).length;
              const warningCount = statusValues.filter(s => s === 2).length;
              const goodCount = statusValues.filter(s => s === 1).length;
              
              let overallStatus = 'Good';
              let overallColor = 'text-green-600';
              
              if (criticalCount > 0) {
                overallStatus = 'Critical';
                overallColor = 'text-red-600';
              } else if (warningCount > 0) {
                overallStatus = 'Warning';
                overallColor = 'text-yellow-600';
              }
              
              return (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.eqpt || item.equipment_name}
                  </td>
                  {statusHeaders.map(header => (
                    <td key={header} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getStatusText(item[header])}
                    </td>
                  ))}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.remarks || '-'}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${overallColor}`}>
                    {overallStatus}
                    <div className="text-xs text-gray-500 mt-1">
                      C:{criticalCount} W:{warningCount} G:{goodCount}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-500">Loading matrix data...</div>;
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-4">
      <div className="flex items-center justify-between mb-4">
         <Button
            label="Select Equipment"
            icon={<Settings size={16} />}
            className="p-button-outlined p-button-sm border-1 rounded-md p-2 bg-[#4F46E5] text-white gap-2 "
            onClick={handleOpenEquipmentModal}
          />
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowDetails(true)}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center"
            disabled={matrixData.length === 0}
          >
            <Eye size={16} className="mr-1" />
            View Details
          </button>
          <button
            onClick={handleDownloadCSV}
            className="text-sm text-gray-600 hover:text-gray-800 font-medium flex items-center"
            title="Download CSV"
            disabled={matrixData.length === 0}
          >
            <FileText size={16} className="mr-1" />
            CSV
          </button>
          <button
            onClick={handleDownloadPNG}
            className="text-sm text-gray-600 hover:text-gray-800 font-medium flex items-center"
            title="Download PNG"
            disabled={matrixData.length === 0}
          >
            <Image size={16} className="mr-1" />
            PNG
          </button>
        </div>
      </div>

      {/* Equipment Selection Section */}
      <div className="mb-4">
       
        
        {/* Selected Equipment Display */}
        {selectedEquipment.length > 0 && (
          <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border">
            {selectedEquipment.map(id => {
              const eqpt = equipmentList.find(e => e.id === id);
              return (
                <span key={id} className="flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                  {eqpt?.name || id}
                  <button
                    type="button"
                    onClick={() => removeEquipment(id)}
                    className="ml-2 text-blue-700 hover:text-blue-900"
                  >
                    <X size={14} />
                  </button>
                </span>
              );
            })}
            <button
              onClick={clearAll}
              className="text-xs text-red-600 hover:text-red-800 ml-2"
            >
              Clear All
            </button>
          </div>
        )}
        
        {selectedEquipment.length === 0 && (
          <div className="p-3 bg-gray-50 rounded-lg border text-sm text-gray-500 text-center">
            No equipment selected. Click "Select Equipment" to choose equipment.
          </div>
        )}
      </div>

      {/* Equipment Selection Modal */}
      {renderEquipmentModal()}

      <Dialog
        visible={showDetails}
        onHide={() => setShowDetails(false)}
        header="Hull Equipment Status Matrix - Detailed View"
        className="w-full max-w-7xl"
        draggable={false}
      >
        {renderDetailsTable()}
      </Dialog>

      {/* Main Matrix Display */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg" ref={tableRef}>
        {matrixData.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-500 bg-gray-50">
            {selectedEquipment.length === 0 ? 'Please select equipment' : 'No data available'}
          </div>
        ) : (
          <div className="min-w-full">
            {/* Header Row */}
            <div className="flex bg-gray-800 text-white sticky top-0 z-10">
              <div className="w-48 px-4 py-3 text-sm font-medium border-r border-gray-600 flex items-center">
                Major Eqpt
              </div>
              {statusHeaders.map(header => (
                <div 
                  key={header} 
                  className="flex-1 min-w-32 px-4 py-3 text-sm font-medium text-center border-r border-gray-600 flex items-center justify-center"
                >
                  {formatHeader(header)}
                </div>
              ))}
            </div>
            
            {/* Data Rows */}
            {matrixData.map((item, idx) => (
              <div key={idx} className="flex border-b border-gray-200 last:border-b-0">
                <div className="w-48 px-4 py-3 text-sm font-medium text-gray-900 bg-white border-r border-gray-200 flex items-center">
                  {item.eqpt || item.equipment_name}
                </div>
                {statusHeaders.map(header => (
                  <div 
                    key={header} 
                    className="flex-1 min-w-32 border-r border-gray-200 last:border-r-0 flex items-center justify-center"
                    style={{ 
                      backgroundColor: getStatusColor(item[header]),
                      minHeight: '48px'
                    }}
                  >
                    <span className="text-white text-xs font-medium opacity-0 hover:opacity-100 transition-opacity">
                      {getStatusText(item[header])}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Legend */}
      {matrixData.length > 0 && (
        <div className="mt-4 flex items-center gap-4 text-sm">
          <span className="text-gray-600 font-medium">Status Legend:</span>
          {[
            ['#22c55e', 'Good'],
            ['#eab308', 'Warning'],
            ['#ef4444', 'Critical'],
            ['#6b7280', 'Unknown']
          ].map(([color, label]) => (
            <div key={label} className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: color }} />
              <span className="text-gray-600">{label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default HullEquipmentStatusMatrix;